import { Box, Button, Card, Flex, Grid, Icon, Text } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteRole, fetchRoleList } from '../../api/classification/classification'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import DiscardModal from '../modals/discardModal'
import AddRoleModal from '../modals/addRoleModal'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import { useRole } from '../../hooks/auth/useRole'

const RolesTable: React.FC = () => {
  const { t } = useTranslation()
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([])
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 30
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editItem, setEditItem] = useState<RowData | null>(null)
  const { isAdmin, isUserMode } = useRole()
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const queryClient = useQueryClient()

  const roles = useQuery({
    queryKey: ['roles', page, searchTerm],
    queryFn: () => fetchRoleList({ search: searchTerm, page, pageSize: itemsPerPage }),
  })

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.fields.title'),
      accessor: 'title',
      type: 'string',
      width: '30%',
      Cell: ({ row }) => <Text textStyle="l-xs" fontWeight="500">{row.title}</Text>,
    },
    {
      label: t('common.fields.description'),
      accessor: 'description',
      type: 'string',
      Cell: ({ row }) => (
        <Text textStyle="l-xs" isTruncated maxW="500px">
          {row.description || t('common.labels.na')}
        </Text>
      ),
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleDelete = () => setShowDiscardModal(true)

  const handleConfirmDelete = async () => {
    setShowDiscardModal(false)
    const roleIdsToDelete = selectedRows
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))

    try {
      await Promise.all(roleIdsToDelete.map((id) => deleteRole(id)))
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    } catch {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    }
    setSelectedRows([])
  }

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedRows(roles.data?.data?.map((row: RowData) => row.id) || [])
    } else {
      setSelectedRows([])
    }
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prev) => [...prev, id])
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
    }
  }

  const allSelected =
    roles.data?.data &&
    roles.data.data.length > 0 &&
    roles.data.data.every((row: RowData) => selectedRows.includes(row.id))
  const isIndeterminate =
    roles.data?.data && roles.data.data.some((row: RowData) => selectedRows.includes(row.id)) && !allSelected

  const handleRowClick = (row: RowData) => {
    setEditItem(row)
    setIsAddOpen(true)
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex gap="8px">
              {selectedRows.length > 0 ? (
                <Button
                  onClick={handleDelete}
                  variant="error"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon fill="none" color="error.100" mr={2}><Trash04Icon /></Icon>
                  {t('workforce.people.deleteCount', { count: selectedRows.length })}
                </Button>
              ) : (
                <>
                  {isAdmin && !isUserMode && (
                    <Button
                      variant="mono"
                      size="sm"
                      h="32px"
                      px="14px"
                      gap="8px"
                      onClick={() => { setEditItem(null); setIsAddOpen(true) }}
                    >
                      <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="white"><PlusIcon /></Icon>
                      {t('common.actions.create')}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {roles.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={roles.data?.data || []}
                onRowClick={handleRowClick}
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={allSelected}
                isIndeterminate={isIndeterminate}
                tableType="roles"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={roles.data?.max_page || 0}
            totalItems={roles.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
      <DiscardModal
        show={showDiscardModal}
        handleClose={() => setShowDiscardModal(false)}
        handleDelete={handleConfirmDelete}
        action="delete"
        selectedRowsCount={selectedRows.length}
      />
      <AddRoleModal
        isOpen={isAddOpen}
        onClose={() => { setIsAddOpen(false); setEditItem(null) }}
        editItem={editItem}
      />
    </Card>
  )
}

export default RolesTable
